/**
 * Normalizes a field name to a human-readable format by:
 * - taking the last path segment after a dot (if present)
 * - inserting spaces between camelCase parts
 * - replacing underscores/hyphens with spaces
 * - lowercasing the result
 * @param {string} fieldName - The field name to normalize (e.g., 'board_id', 'customFields.carId')
 * @returns {string} - The human-readable field name
 */
const normalizeFieldName = (field) => {
  if (!field) return '';

  const fieldName = typeof field === 'string' ? field : field.label;
  // Extract last segment after dots (e.g., 'customFields.carId' → 'carId')
  const lastDotIndex = fieldName.lastIndexOf('.');
  const fieldPart =
    lastDotIndex === -1 ? fieldName : fieldName.slice(lastDotIndex + 1);

  // Replace separators with spaces
  let normalized = fieldPart;
  if (normalized.includes('_') || normalized.includes('-')) {
    normalized = normalized.replace(/_/g, ' ').replace(/-/g, ' ');
  }

  // Insert space before uppercase letters (camelCase)
  let result = '';
  for (let i = 0; i < normalized.length; i += 1) {
    const char = normalized[i];
    const isUpperCase = char >= 'A' && char <= 'Z';
    const prevChar = normalized[i - 1];
    const isPrevLowerCaseOrNumber =
      prevChar &&
      ((prevChar >= 'a' && prevChar <= 'z') ||
        (prevChar >= '0' && prevChar <= '9'));

    const shouldAddSpace = isUpperCase && isPrevLowerCaseOrNumber;

    if (shouldAddSpace) {
      result += ' ';
    }
    result += char;
  }

  // Clean up and normalize
  return result.split(' ').filter(Boolean).join(' ').toLowerCase();
};

/**
 * Generates a search placeholder string from an array of field names
 * @param {string[]} fields - Array of field names to generate placeholder from
 * @param {string} externalPlaceholder - Optional external placeholder to use instead of generating one
 * @returns {string} - The formatted search placeholder string
 * @example
 * generateSearchPlaceholder(['board_id', 'customFields.carId', 'customFields.subscriptionId'])
 * // Returns: 'Search by board id, car id, subscription id'
 */
const generateSearchPlaceholder = (fields = [], externalPlaceholder = '') => {
  if (externalPlaceholder) {
    return externalPlaceholder;
  }
  if (!Array.isArray(fields) || fields.length === 0) {
    return 'Search';
  }

  const normalizedFields = fields.map(normalizeFieldName).filter(Boolean);

  if (normalizedFields.length === 0) {
    return 'Search';
  }

  const uniqueFields = [...new Set(normalizedFields)];

  return `Search by ${uniqueFields.join(', ')}`;
};

export default generateSearchPlaceholder;
